import React, { useRef, useState, useEffect } from "react";
import "./DialogPreview.css";
import ColorParse from "../ColorParse";

export default function DialogPreview({ config, setConfig, textures, selectedItem }) {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, content: null });

  useEffect(() => {
    function updateScale() {
      if (!containerRef.current) return;
      const parentWidth = containerRef.current.parentElement?.offsetWidth ?? 800;
      let newScale = (parentWidth * 0.5) / 400;

      const MIN_SCALE = 1;
      const MAX_SCALE = 1.5;
      newScale = Math.min(Math.max(newScale, MIN_SCALE), MAX_SCALE);

      setScale(newScale);
    }

    window.addEventListener("resize", updateScale);
    updateScale();

    return () => window.removeEventListener("resize", updateScale);
  }, []);

  const handleMouseMove = (e) => {
    setTooltip((prev) => ({
      ...prev,
      x: e.clientX + 12,
      y: e.clientY + 12,
    }));
  };

  const getItemTexture = (material) => {
    if (!material || typeof material !== "string") return null;
    const fileName = `${material.toLowerCase()}.png`;
    return textures?.find((t) => t.fileName === fileName) ?? null;
  };

  const renderItem = (item, key) => {
    if (!item || typeof item !== "object") return null;
    const { type } = item;

    switch (type) {
      case "text":
        return (
          <div key={key} className="dialog-text">
            {ColorParse(item?.name ?? "")}
          </div>
        );

      case "item": {
        const texture = getItemTexture(item.material);
        return (
          <div key={key} className="dialog-item-wrapper">
            <div className="dialog-item">
              {texture && (
                <img
                  src={texture.url}
                  alt={item?.name ?? "Item"}
                  className="dialog-item-image"
                  onMouseEnter={(e) => {
                    setTooltip({
                      visible: true,
                      x: e.clientX + 12,
                      y: e.clientY + 12,
                      content: (
                        <div className="tooltip-content">
                          <div className="tooltip-name">{ColorParse(item?.name ?? "")}</div>
                        </div>
                      ),
                    });
                  }}
                  onMouseLeave={() =>
                    setTooltip({ visible: false, x: 0, y: 0, content: null })
                  }
                  onMouseMove={handleMouseMove}
                />
              )}
            </div>
            <div className="dialog-text item">
              {ColorParse(item?.text ?? "")}
            </div>
          </div>
        );
      }

      case "input":
        return (
          <>
            <div key={key + "-text"} className="dialog-text input">
              {ColorParse(item?.name ?? "")}
            </div>
            <div key={key + "-input"} className="dialog-button input">
              {ColorParse(item?.initial || "Type Here...")}
            </div>
          </>
        );

      case "option":
        return (
          <div key={key} className="dialog-button option">
            {ColorParse(item?.name ?? "")}: {item?.options?.[0] ?? "None"}
          </div>
        );

      case "range":
        return (
          <div key={key} className="dialog-button range">
            <div className="range-indicator"></div>
            {ColorParse(item?.name ?? "")}: {item?.initial ?? 0}
          </div>
        );

      case "boolean":
        return (
          <div key={key} className="dialog-boolean-wrapper">
            <div className="dialog-button boolean" />
            <div className="dialog-text boolean">
              {ColorParse(item?.name ?? "")}
            </div>
          </div>
        );

      case "button":
        return (
          <div
            key={key}
            className="dialog-button"
            onMouseEnter={(e) => {
              if (item?.tooltip) {
                setTooltip({
                  visible: true,
                  x: e.clientX + 12,
                  y: e.clientY + 12,
                  content: (
                    <div className="tooltip-name">
                      {ColorParse(item.tooltip ?? "")}
                    </div>
                  ),
                });
              }
            }}
            onMouseLeave={() =>
              setTooltip({ visible: false, x: 0, y: 0, content: null })
            }
            onMouseMove={handleMouseMove}
          >
            {ColorParse(item?.name ?? "")}
          </div>
        );

      default:
        return null;
    }
  };

    const layoutKeys = Object.keys(config?.layout ?? {}).sort((a, b) => parseInt(a) - parseInt(b));
    const rawOrderedItems = layoutKeys.map((key) => {
      const itemKeys = config?.layout?.[key] ?? [];
      const firstKey = itemKeys[0];
      return config?.items?.[firstKey];
    }).filter(Boolean);

    // Categorize into groups: text/item → other → button
    const topItems = [];
    const middleItems = [];
    const bottomItems = [];

    for (const item of rawOrderedItems) {
      if (!item || typeof item !== "object") continue;

      if (item.type === "text" || item.type === "item") {
          topItems.push(item);
      } else if (item.type === "button" || !item.type) {
          // Treat missing type as "button"
          bottomItems.push(item);
      } else {
          middleItems.push(item);
      }
    }

    const orderedItems = [...topItems, ...middleItems, ...bottomItems];


  return (
    <div className="dialog-container">
      <div className="dialog-panel" ref={containerRef} style={{ "--scale": scale }}>
        <div className="dialog-title">{ColorParse(config?.title || "Dialog Title")}</div>
        <div className="dialog-body">
          {orderedItems.map((item, index) => renderItem(item, index))}
        </div>

        {tooltip.visible && (
          <div
            className="minecraft-tooltip"
            style={{
              top: tooltip.y,
              left: tooltip.x,
              position: "fixed",
              pointerEvents: "none",
              zIndex: 1000,
            }}
          >
            {tooltip.content}
          </div>
        )}
      </div>
    </div>
  );
}
